/**
 * UtilityTypes
 * From: 1.0.0
 *
 * @packageDocumentation
 */

/**
 * overwrite O with T
 */
export type Overwrite<T, U = Record<string, unknown>> = Omit<T, keyof U> & U;
/**
 * object type
 */
export type ObjectType = Record<string | number | symbol, unknown>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UnsafeObjectType = Record<string | number | symbol, any>;

/**
 * represents a generic type of promise `P`
 */
export type ThenType<P> = P extends Promise<infer R> ? R : never;
/**
 * represents a generic type of promise that is returned from a function `F`
 */
export type ReturnThenType<F extends (...args: unknown[]) => unknown> =
  ThenType<ReturnType<F>>;

/**
 * represents a nil value type that is undefined, null or void
 */
export type Nil = undefined | null | void;

// ----------------------------------------------------------------------------
// TypeUtils
// ----------------------------------------------------------------------------

/**
 * boolean ないし Boolean であることを判定する
 *
 * @param value -
 * @returns
 */
const isBoolean = (value: unknown): value is boolean =>
  typeof value === 'boolean' ||
  Object.prototype.toString.call(value) === '[object Boolean]';

/**
 * Date であることを判定する
 *
 * @param value -
 * @returns
 */
const isDate = (value: unknown): value is Date =>
  typeof value === 'object' &&
  Object.prototype.toString.call(value) === '[object Date]';

/**
 *
 * @param value -
 * @returns
 */
const isMap = <K = unknown, V = unknown>(value: unknown): value is Map<K, V> =>
  typeof value === 'object' &&
  Object.prototype.toString.call(value) === '[object Map]';

/**
 * undefined ないし null, void であるか判定する
 *
 * @param value - 値
 * @returns
 */
const isNil = (value: unknown): value is Nil =>
  value === undefined || value === null || value === void 0;

/**
 * isNil に加えて，配列の場合は空配列であることを判定する
 *
 * @param value -
 * @returns
 */
const isNilArray = (value: unknown): value is Nil =>
  isNil(value) || (Array.isArray(value) ? value.length === 0 : false);

/**
 * isNil に加えて，Map の場合は空であることを判定する
 *
 * @param value -
 * @returns
 */
const isNilMap = (value: unknown): value is Nil =>
  isNil(value) || (isMap(value) ? value.size === 0 : false);

/**
 * isNil に加えて，数値の場合はNaNであることを判定する
 *
 * @param value -
 * @returns
 */
const isNilNumber = (value: unknown): value is Nil =>
  isNil(value) || (isNumber(value) ? Number.isNaN(value) : false);

/**
 * isNil に加えて，Object の場合は空であることを判定する
 *
 * @param value -
 * @returns
 */
const isNilObject = (value: unknown): value is Nil =>
  isNil(value) || (isObject(value) ? Object.keys(value).length === 0 : false);

/**
 * isNil に加えて，Set の場合は空であることを判定する
 *
 * @param value -
 * @returns
 */
const isNilSet = (value: unknown): value is Nil =>
  isNil(value) || (isSet(value) ? value.size === 0 : false);

/**
 * undefined, null, void または，
 * - 文字列の場合は空文字列
 * - 数値の場合はNaN
 * - 配列の場合は空配列
 * - オブジェクトの場合は空オブジェクト
 *
 * であることを判定する
 *
 * @param value -
 * @returns
 */
const isNilStrict = (value: unknown): value is Nil =>
  isNil(value) ||
  (isString(value)
    ? value.length === 0
    : isNumber(value)
      ? Number.isNaN(value)
      : Array.isArray(value)
        ? value.length === 0
        : isObject(value)
          ? Object.keys(value).length === 0
          : false);

/**
 * isNil に加えて，文字列の場合は空文字列であることを判定する
 *
 * @param value -
 * @returns
 */
const isNilString = (value: unknown): value is Nil =>
  isNil(value) || (isString(value) ? value.length === 0 : false);

/**
 * undefined ないし null, void ではないことを判定する．
 * `!isNil(value)` に等しい．
 *
 * @param value -
 * @returns
 */
const isNonNil = <T>(value: T | Nil): value is T => !isNil(value);

/**
 * 有効な配列であることを判定する．
 * `!isNilArray(value)` とは異なる．
 *
 * @param value -
 * @returns
 */
const isNonNilArray = <T>(value: Array<T> | Nil): value is Array<T> =>
  Array.isArray(value) ? !isNilArray(value) : false;

/**
 * 有効なMapであることを判定する．
 * `!isNilMap` とは異なる．
 *
 * @param value -
 * @returns
 */
const isNonNilMap = <K, V>(value: Map<K, V> | Nil): value is Map<K, V> =>
  isMap(value) ? !isNilMap(value) : false;

/**
 * 有効な数値であることを判定する．
 * `!isNilNumber(value)`とは異なる．
 *
 * @param value -
 * @returns
 */
const isNonNilNumber = (value: unknown): value is number =>
  isNumber(value) ? !isNilNumber(value) : false;

/**
 * 有効な Object であることを判定する．
 * `!isNilObject(value)` とは異なる．
 *
 * @param value -
 * @returns
 */
const isNonNilObject = (value: unknown): value is object =>
  isObject(value) ? !isNilObject(value) : false;

/**
 * 有効な Set であることを判定する．
 * `!isNilSet(value)` とは異なる．
 *
 * @param value -
 * @returns
 */
const isNonNilSet = <T = unknown>(value: Set<T> | Nil): value is Set<T> =>
  isSet(value) ? !isNilSet(value) : false;

/**
 * undefined, null, void ではなく，かつ
 * - 文字列の場合は空文字列
 * - 数値の場合はNaN
 * - 配列の場合は空配列
 * - オブジェクトの場合は空オブジェクト
 *
 * でもないことを判定する．
 * `!isNilStrict(value)` に等しい．
 *
 * @param value -
 * @returns
 */
const isNonNilStrict = <T>(value: T | Nil): value is T => !isNilStrict(value);

/**
 * 有効な文字列であることを判定する．
 * `!isNilString(value)`とは異なる．
 *
 * @param value -
 * @returns
 */
const isNonNilString = (value: unknown): value is string =>
  isString(value) ? !isNilString(value) : false;

/**
 * number ないし Number であることを判定する
 *
 * @param value -
 * @returns
 */
const isNumber = (value: unknown): value is number =>
  typeof value === 'number' ||
  Object.prototype.toString.call(value) === '[object Number]';

/**
 * Object であることを判定する．
 * object ではないので，Array や Date 等は含まない．
 *
 * @param value -
 * @returns
 */
const isObject = (value: unknown): value is object =>
  typeof value === 'object' &&
  Object.prototype.toString.call(value) === '[object Object]';

/**
 * Set であることを判定する
 *
 * @param value -
 * @returns
 */
const isSet = <T = unknown>(value: unknown): value is Set<T> =>
  typeof value === 'object' &&
  Object.prototype.toString.call(value) === '[object Set]';

/**
 * string ないし String であることを判定する
 *
 * @param value -
 * @returns
 */
const isString = (value: unknown): value is string =>
  typeof value === 'string' ||
  Object.prototype.toString.call(value) === '[object String]';

/**
 * 有効ではない値はすべて undefined に変換する
 *
 * @param value -
 * @returns
 */
const toStrictValue = <T>(value: T | Nil) =>
  isNilStrict(value) ? undefined : value;

export const TypeUtils = {
  isBoolean,
  isDate,
  isMap,
  isNil,
  isNilArray,
  isNilMap,
  isNilNumber,
  isNilObject,
  isNilSet,
  isNilStrict,
  isNilString,
  isNonNil,
  isNonNilArray,
  isNonNilMap,
  isNonNilNumber,
  isNonNilObject,
  isNonNilSet,
  isNonNilStrict,
  isNonNilString,
  isNumber,
  isObject,
  isSet,
  isString,
  toStrictValue,
};
